/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-17 12:30:39
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { v4 } from 'uuid';
import { SubmissionError } from 'redux-form';
import { SORT_TYPES_UNIQUE, DEFAULT_SORT_TYPES } from 'src/constants/custom-sorting';
import { checkHasSpecialCharacters } from 'src/helpers';

/**
 * Build sort types option
 *
 * @param  {Object} customSorting custom sorting from API
 *
 * @return {Object} initialValue
 */
export const buildSortTypesOption = (
  sortTypes: Array<Object>,
  sortOptions: Object,
  sortOption: Object
) => {
  let result = [...sortTypes];
  const { type: itemType = '' } = sortOption;

  _.map(sortOptions, (sortItem, key) => {
    const { type } = sortItem;
    if (type !== itemType && type !== 'tag') {
      // Type is unique -> remove type from result
      if (SORT_TYPES_UNIQUE.includes(type)) {
        _.remove(result, item => item.value === type);
      } else {
        // Filter sorts using the same type with current item
        const sortFilter = _.filter(sortOptions, item => item.type === type);

        // Check number of sort using this type
        const limitCount = _.startsWith(type, 'metafield-') ? 4 : 2;
        if (sortFilter.length === limitCount && sortFilter[0].type === type) {
          // Remove type form result
          _.remove(result, item => item.value === type);
        }
      }
    }
  });

  return result;
};

/**
 * Get name of current sort type
 *
 * @param  {string} type type of sort
 * @param  {Object} sortTypes list of sort type
 *
 * @return {Object} initialValue
 */
export const getTypeValue = (type: string, sortTypes: Array<Object>): string => {
  if (_.isEmpty(type)) {
    return '';
  }
  const sortType = _.find(sortTypes, item => item.value === type);
  return sortType ? sortType.type : '';
};

/**
 * Build initial data for custom sorting list
 *
 * @param  {Object} customSorting custom sorting from API
 *
 * @return {Object} initialValue
 */
export const buildInitialCustomSortingValues = (
  customSorting: Object,
  collections: Array<Object>
) => {
  const { sortOptions, defaultSorting: initialDefaultSorting = {} } = customSorting;

  // Update specificCollections to keep label in form
  let specificCollections = _.get(initialDefaultSorting, 'collection.specific', []);
  specificCollections = _.map(specificCollections, item => {
    const { collectionIds: collectionIdsList = [] } = item;
    let collectionIds = [];
    _.map(collectionIdsList, collectionId => {
      const collection = _.find(collections, collection => collection.value === collectionId);
      if (!_.isEmpty(collection)) {
        collectionIds.push(collection);
      }
    });
    return { ...item, collectionIds };
  });

  const defaultSorting = {
    status: _.get(initialDefaultSorting, 'status', false),
    statusSearch: _.get(initialDefaultSorting, 'search.status', false),
    search: _.get(initialDefaultSorting, 'search.sortId', ''),
    statusCollection: _.get(initialDefaultSorting, 'collection.status', false),
    allCollection: _.get(initialDefaultSorting, 'collection.all.sortId', ''),
    specificCollections
  };
  return { sortOptions, defaultSorting };
};

/**
 * Build initial data for sort option
 *
 * @param  {Object} sortOption sortOption from API
 *
 * @return {Object} initialValue
 */
export const buildInitialSortOption = (sortOption: Object) => {
  let result = { ...sortOption };
  result.status = _.isEmpty(result.status) ? 'active' : result.status;
  return result;
};

/**
 * Build data for sort option
 *
 * @param  {Object} data custom sorting data
 * @param  {Object} parseParams parse params from API
 *
 * @return {Object} value value for sending API
 */
export const buildSortOptionValue = (data: Object) => {
  const result = Object.assign({}, data);
  // Check id  data
  const { id = '' } = result;
  if (id === '') {
    result.id = v4();
  }

  return {
    ...result
  };
};

/**
 * Build data for custom sorting list
 *
 * @param  {Object} data custom sorting data
 * @param  {Object} parseParams parse params from API
 *
 * @return {Object} value value for sending API
 */
export const buildCustomSortingValues = (data: Object, positionFlag: boolean) => {
  // Convert data

  const { sortOptions = [], defaultSorting = {} } = data;

  if (positionFlag) {
    _.map(sortOptions, (item, index) => {
      item.position = index;
    });
  }

  // Convert default sorting
  const {
    allCollection = '',
    search = '',
    specificCollections = [],
    status = false,
    statusCollection = false,
    statusSearch = false
  } = defaultSorting;

  const specific = _.map(specificCollections, item => {
    const { sortId = '', collectionIds: collectionIdsList = [] } = item;
    const collectionIds = _.map(collectionIdsList, collection => collection.value);
    return { sortId, collectionIds };
  });
  return {
    sortOptions,
    defaultSorting: {
      status,
      search: {
        sortId: search,
        status: statusSearch
      },
      collection: {
        status: statusCollection,
        all: { sortId: allCollection },
        specific
      }
    }
  };
};

/**
 * Check validate custom sorting item
 *
 * @param  {Object} data sort option
 * @param  {Object} customSorting custom sorting value
 * @param  {number} index index of sort option
 *
 * @return {string} checked result
 *
 */
export const validateSortOptionBeforeSave = (
  data: Object,
  sortOptions: Object,
  index: number,
  i18n: any
) => {
  const { label = '', type = '', order = '', tagPrefix = '', dataType = '' } = data;
  let labelValidate = '';
  let checkSpecialCharactersValue = checkHasSpecialCharacters(label);
  if (checkSpecialCharactersValue) {
    labelValidate = i18n.translate(
      'AdminHTML.CustomSorting.Validate.invalid_Label_SpecialCharacter'
    );
  }

  if (label.length > 128) {
    labelValidate = i18n.translate('AdminHTML.CustomSorting.Validate.invalid_Label_Length');
  }

  // Check label is required if type is in default list
  if (!DEFAULT_SORT_TYPES.includes(type)) {
    if (_.isEmpty(label)) {
      labelValidate = i18n.translate('AdminHTML.CustomSorting.Validate.invalid_Label_Empty');
    }
  }

  if (!_.isEmpty(labelValidate)) {
    throw new SubmissionError({
      label: labelValidate
    });
  }

  if (!SORT_TYPES_UNIQUE.includes(type)) {
    if (_.isEmpty(order)) {
      throw new SubmissionError({
        order: i18n.translate('AdminHTML.CustomSorting.Validate.invalid_Order_Empty')
      });
    }
  }

  const sortOptionItems = _.find(sortOptions, (item, itemIndex) => {
    // Special validation for metafield option
    if (_.startsWith(type, 'metafield-')) {
      return (
        item.type === type &&
        item.order === order &&
        item.dataType === dataType &&
        itemIndex !== index
      );
    }

    return item.type === type && item.order === order && type !== 'tag' && itemIndex !== index;
  });

  if (!_.isEmpty(sortOptionItems)) {
    throw new SubmissionError({
      order: i18n.translate('AdminHTML.CustomSorting.Validate.invalid_Type_Existed')
    });
  }

  if (type === 'tag') {
    let tagPrefixValidate = '';
    checkSpecialCharactersValue = checkHasSpecialCharacters(tagPrefix);
    if (checkSpecialCharactersValue) {
      tagPrefixValidate = i18n.translate(
        'AdminHTML.CustomSorting.Validate.invalid_TagPrefix_SpecialCharacter'
      );
    }

    if (tagPrefix.length > 128) {
      tagPrefixValidate = i18n.translate(
        'AdminHTML.CustomSorting.Validate.invalid_TagPrefix_Length'
      );
    }

    if (!_.isEmpty(tagPrefixValidate)) {
      throw new SubmissionError({
        tagPrefix: tagPrefixValidate
      });
    }

    const sortOptionItems = _.find(
      sortOptions,
      (item, itemIndex) =>
        item.type === type &&
        item.order === order &&
        item.tagPrefix === tagPrefix &&
        itemIndex !== index
    );
    if (!_.isEmpty(sortOptionItems)) {
      if (_.isEmpty(order)) {
        throw new SubmissionError({
          tagPrefix: i18n.translate('AdminHTML.CustomSorting.Validate.invalid_TagPrefix_Existed')
        });
      }
    }

    // Validate value format
    let dataTypeValidate = '';
    if (!_.isEmpty(tagPrefix) && _.isEmpty(dataType)) {
      dataTypeValidate = i18n.translate('AdminHTML.CustomSorting.Validate.invalid_DataType_Empty');
    }

    if (!_.isEmpty(dataTypeValidate)) {
      throw new SubmissionError({
        dataType: dataTypeValidate
      });
    }
  }

  if (_.includes(type, 'metafield-')) {
    // Validate value format
    let dataTypeValidate = '';
    if (_.isEmpty(dataType)) {
      dataTypeValidate = i18n.translate('AdminHTML.CustomSorting.Validate.invalid_DataType_Empty');
    }

    if (!_.isEmpty(dataTypeValidate)) {
      throw new SubmissionError({
        dataType: dataTypeValidate
      });
    }
  }

  return true;
};

/**
 * Check validate specific collections item for sorting
 *
 * @param  {string} data collections - sort
 *
 * @return {string} checked result
 *
 */
export const validateSpecificCollectionsItemBeforeSave = (data: Object, i18n: Object) => {
  const { sortId = '', collectionIds = [] } = data;
  if (_.isEmpty(sortId)) {
    throw new SubmissionError({
      sortId: i18n.translate('AdminHTML.CustomSorting.Validate.invalid_sortingOption_Empty')
    });
  }
  if (_.isEmpty(collectionIds)) {
    throw new SubmissionError({
      collectionIds: i18n.translate('AdminHTML.CustomSorting.Validate.invalid_Collections_Empty')
    });
  }
  return true;
};
